/**
 * Zaviago Design Tokens
 * iPadOS-inspired design system tokens
 */

export const designTokens = {
  colors: {
    // Primary palette
    primary: {
      50: "#f0f9ff",
      100: "#e0f2fe",
      200: "#bae6fd",
      300: "#7dd3fc",
      400: "#38bdf8",
      500: "#0ea5e9",
      600: "#0284c7",
      700: "#0369a1",
      800: "#075985",
      900: "#0c4a6e",
    },
    // Neutral palette
    neutral: {
      50: "#fafafa",
      100: "#f5f5f5",
      200: "#e5e5e5",
      300: "#d4d4d4",
      400: "#a3a3a3",
      500: "#737373",
      600: "#525252",
      700: "#404040",
      800: "#262626",
      900: "#171717",
    },
  },

  /**
   * @deprecated Use Tailwind utility classes instead (rounded-xs, rounded-sm, rounded, rounded-md, rounded-lg, rounded-xl, rounded-2xl, rounded-3xl).
   * Source of truth for border-radius is tailwind.config.ts borderRadius.
   * Kept for reference only — do not import for new code.
   */
  radius: {
    none: "0px",
    sm: "8px",
    DEFAULT: "16px",
    md: "16px",
    lg: "24px",
    xl: "32px",
    full: "9999px",
  },

  shadows: {
    // Apple-style shadows - soft, diffused, elegant
    apple: "0 2px 8px rgba(0, 0, 0, 0.08), 0 1px 4px rgba(0, 0, 0, 0.04)",
    "apple-md": "0 4px 16px rgba(0, 0, 0, 0.1), 0 2px 8px rgba(0, 0, 0, 0.06)",
    "apple-lg": "0 8px 32px rgba(0, 0, 0, 0.12), 0 4px 16px rgba(0, 0, 0, 0.08)",
    "apple-xl": "0 12px 48px rgba(0, 0, 0, 0.15), 0 6px 24px rgba(0, 0, 0, 0.1)",
    // Legacy shadows (kept for compatibility)
    sm: "0 1px 2px 0 rgba(0, 0, 0, 0.05)",
    DEFAULT: "0 2px 8px rgba(0, 0, 0, 0.08), 0 1px 4px rgba(0, 0, 0, 0.04)",
    md: "0 4px 16px rgba(0, 0, 0, 0.1), 0 2px 8px rgba(0, 0, 0, 0.06)",
    lg: "0 8px 32px rgba(0, 0, 0, 0.12), 0 4px 16px rgba(0, 0, 0, 0.08)",
    xl: "0 12px 48px rgba(0, 0, 0, 0.15), 0 6px 24px rgba(0, 0, 0, 0.1)",
    glass: "0 8px 32px rgba(0, 0, 0, 0.1), 0 4px 16px rgba(0, 0, 0, 0.06)",
    "glass-sm": "0 4px 16px rgba(0, 0, 0, 0.08), 0 2px 8px rgba(0, 0, 0, 0.04)",
  },

  blur: {
    none: "0px",
    sm: "4px",
    DEFAULT: "12px",
    md: "12px",
    lg: "16px",
    xl: "24px",
  },

  spacing: {
    // iPadOS-style spacing scale
    0: "0px",
    1: "0.25rem",
    2: "0.5rem",
    3: "0.75rem",
    4: "1rem",
    5: "1.25rem",
    6: "1.5rem",
    8: "2rem",
    10: "2.5rem",
    12: "3rem",
    16: "4rem",
    18: "4.5rem",
    20: "5rem",
    22: "5.5rem",
    24: "6rem",
    26: "6.5rem",
    28: "7rem",
    30: "7.5rem",
    32: "8rem",
  },

  typography: {
    fontFamily: {
      sans: [
        "-apple-system",
        "BlinkMacSystemFont",
        '"SF Pro Display"',
        '"SF Pro Text"',
        "Inter",
        "system-ui",
        "sans-serif",
      ],
      mono: [
        '"SF Mono"',
        "Monaco",
        "Menlo",
        "Consolas",
        "monospace",
      ],
    },
    fontSize: {
      xs: ["0.75rem", { lineHeight: "1rem" }],
      sm: ["0.875rem", { lineHeight: "1.25rem" }],
      base: ["1rem", { lineHeight: "1.5rem" }],
      lg: ["1.125rem", { lineHeight: "1.75rem" }],
      xl: ["1.25rem", { lineHeight: "1.875rem" }],
      "2xl": ["1.5rem", { lineHeight: "2rem" }],
      "3xl": ["1.875rem", { lineHeight: "2.25rem" }],
      "4xl": ["2.25rem", { lineHeight: "2.5rem" }],
      "5xl": ["3rem", { lineHeight: "1" }],
    },
    fontWeight: {
      light: "300",
      normal: "400",
      medium: "500",
      semibold: "600",
      bold: "700",
    },
  },

  borders: {
    sidebar: `1px solid hsl(214.3 31.8% 91.4%)`, // matches CSS --border (Tailwind border-border)
    card: `1px solid hsl(214.3 31.8% 91.4%)`, // matches CSS --border — tooltips, cards, panels
  },

  /**
   * Card system — centralized Tailwind class tokens for all section cards.
   *
   * Every detail-page card (item, sales order, stock entry, pricing rule wizard)
   * MUST use these tokens. Changing the card style once here propagates everywhere.
   *
   * Usage:  import { CARD, CARD_CLICKABLE } from "@/config/design-tokens";
   *         <div className={CARD}>…</div>
   *         <div className={cn(CARD, "p-6")}>…</div>
   */
  card: {
    /** Base card: rounded-xl border with subtle shadow.
     *  Uses overflow-clip (NOT overflow-hidden) so wheel events propagate
     *  to the parent scroll container instead of being trapped by the card. */
    base: "rounded-xl border border-border shadow-sm bg-card overflow-clip",
    /** Clickable card: cursor-pointer + instant press feedback for perceived responsiveness */
    clickable: "rounded-xl border border-border shadow-sm bg-card overflow-clip cursor-pointer active:scale-[0.98] transition-transform duration-100",
    /** Card with custom background (no bg-card) */
    transparent: "rounded-xl border border-border shadow-sm overflow-clip",
  },

  motion: {
    duration: {
      fast: "150ms",
      normal: "300ms",
      slow: "500ms",
      slower: "700ms",
    },
    easing: {
      default: "cubic-bezier(0.4, 0, 0.2, 1)",
      in: "cubic-bezier(0.4, 0, 1, 1)",
      out: "cubic-bezier(0, 0, 0.2, 1)",
      inOut: "cubic-bezier(0.4, 0, 0.2, 1)",
      smooth: "cubic-bezier(0.4, 0, 0.2, 1)",
    },
  },
} as const;

export type DesignTokens = typeof designTokens;

// ── Card class shortcuts ────────────────────────────────────
// Import these directly: import { CARD, CARD_CLICKABLE } from "@/config/design-tokens";
export const CARD = designTokens.card.base;
export const CARD_CLICKABLE = designTokens.card.clickable;
export const CARD_TRANSPARENT = designTokens.card.transparent;

